// =====================================================
// Swiss Finance OS - Realtime Updates Hook
// React hook for managing realtime subscriptions
// =====================================================

import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { realtimeService, RealtimeUpdate } from '@/services/realtime';
import { useToast } from '@/hooks/use-toast';
import { applyRealtimeCacheUpdate } from '@/lib/query-cache-manager';
import { supabase } from '@/integrations/supabase/client';

interface UseRealtimeUpdatesOptions {
  eventTypes?: RealtimeUpdate['type'][] | 'all';
  showToasts?: boolean;
  onUpdate?: (update: RealtimeUpdate) => void;
  autoConnect?: boolean;
}

interface UseRealtimeUpdatesReturn {
  updates: RealtimeUpdate[];
  latestUpdate: RealtimeUpdate | null;
  isConnected: boolean;
  clearUpdates: () => void;
  addUpdate: (update: RealtimeUpdate) => void;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

export default function useRealtimeUpdates(
  options: UseRealtimeUpdatesOptions = {}
): UseRealtimeUpdatesReturn {
  const {
    eventTypes = 'all',
    showToasts = false,
    onUpdate,
    autoConnect = true
  } = options;

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [updates, setUpdates] = useState<RealtimeUpdate[]>([]);
  const [latestUpdate, setLatestUpdate] = useState<RealtimeUpdate | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const unsubscribeFunctionsRef = useRef<(() => void)[]>([]);
  const connectionCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showUpdateToast = useCallback((update: RealtimeUpdate) => {
    switch (update.type) {
      case 'transaction_processed':
        toast({
          title: 'Transaction Processed',
          description: 'A new transaction has been processed successfully.',
          duration: 3000,
        });
        break;

      case 'compliance_alert': {
        const alert = update.data;
        toast({
          title: alert.severity === 'urgent' ? '🚨 Urgent Alert' : '⚠️ Compliance Alert',
          description: alert.message,
          variant: alert.severity === 'urgent' || alert.severity === 'high' ? 'destructive' : 'default',
          duration: alert.severity === 'urgent' ? 0 : 5000,
        });
        break;
      }

      case 'processing_status': {
        const status = update.data;
        if (status.status === 'completed') {
          toast({
            title: '✅ Processing Complete',
            description: 'File processing completed successfully.',
            duration: 3000,
          });
        } else if (status.status === 'error') {
          toast({
            title: '❌ Processing Error',
            description: 'File processing encountered an error.',
            variant: 'destructive',
            duration: 5000,
          });
        }
        break;
      }

      case 'team_activity': {
        const activity = update.data;
        toast({
          title: '👥 Team Activity',
          description: `${activity.userName} ${getActivityDescription(activity.action)}`,
          duration: 2000,
        });
        break;
      }

      case 'file_upload_status': {
        const upload = update.data;
        if (upload.status === 'completed') {
          toast({
            title: '📁 Upload Complete',
            description: `${upload.fileName} uploaded successfully.`,
            duration: 3000,
          });
        }
        break;
      }
    }
  }, [toast]);

  const handleUpdate = useCallback((update: RealtimeUpdate) => {
    setUpdates(prev => [update, ...prev.slice(0, 99)]); // Keep last 100 updates
    setLatestUpdate(update);

    if (showToasts) {
      showUpdateToast(update);
    }

    if (onUpdate) {
      onUpdate(update);
    }

    applyRealtimeCacheUpdate(queryClient, update);
  }, [onUpdate, showToasts, showUpdateToast, queryClient]);

  const getActivityDescription = (action: string): string => {
    switch (action) {
      case 'file_uploaded':
        return 'uploaded a file';
      case 'transaction_validated':
        return 'validated transactions';
      case 'export_completed':
        return 'completed an export';
      case 'login':
        return 'logged in';
      case 'logout':
        return 'logged out';
      default:
        return 'performed an action';
    }
  };

  const connect = useCallback(async () => {
    try {
      await realtimeService.initialize();
      setIsConnected(true);

      // Subscribe to events
      const normalizedEventTypes: Array<'all' | RealtimeUpdate['type']> =
        eventTypes === 'all' ? ['all'] : eventTypes;

      const unsubscribeFunctions = normalizedEventTypes.map((eventType) =>
        realtimeService.subscribe(eventType, handleUpdate)
      );

      unsubscribeFunctionsRef.current = unsubscribeFunctions;

      console.log('Realtime updates connected');
    } catch (error) {
      console.error('Failed to connect to realtime updates:', error);
      setIsConnected(false);
    }
  }, [eventTypes, handleUpdate]);

  const disconnect = useCallback(async () => {
    // Unsubscribe from all events
    unsubscribeFunctionsRef.current.forEach(unsubscribe => unsubscribe());
    unsubscribeFunctionsRef.current = [];

    await realtimeService.cleanup();
    setIsConnected(false);
    console.log('Realtime updates disconnected');
  }, []);

  const clearUpdates = useCallback(() => {
    setUpdates([]);
    setLatestUpdate(null);
  }, []);

  const addUpdate = useCallback((update: RealtimeUpdate) => {
    handleUpdate(update);
  }, [handleUpdate]);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  // Check connection status with debounce to prevent flickering
  useEffect(() => {
    let stableConnectionStatus = isConnected;
    let pendingStatus: boolean | null = null;

    const checkConnection = () => {
      const currentStatus = realtimeService.getConnectionStatus();
      
      // If status changed, wait for it to be stable
      if (currentStatus !== stableConnectionStatus) {
        if (pendingStatus === currentStatus) {
          // Status has been the same for 2 checks, update UI
          stableConnectionStatus = currentStatus;
          setIsConnected(currentStatus);
          pendingStatus = null;
        } else {
          // First time seeing this status, store it
          pendingStatus = currentStatus;
        }
      } else {
        // Reset pending if status matches current
        pendingStatus = null;
      }
    };

    // Check less frequently (every 10 seconds instead of 5)
    const interval = setInterval(checkConnection, 10000);
    return () => clearInterval(interval);
  }, [isConnected]);

  return {
    updates,
    latestUpdate,
    isConnected,
    clearUpdates,
    addUpdate,
    connect,
    disconnect,
  };
}

// Specialized hooks for specific use cases
export function useTransactionUpdates() {
  return useRealtimeUpdates({
    eventTypes: ['transaction_processed'],
    showToasts: true,
  });
}

export function useProcessingStatus() {
  return useRealtimeUpdates({
    eventTypes: ['processing_status', 'file_upload_status'],
    showToasts: true,
  });
}

export function useComplianceAlerts() {
  return useRealtimeUpdates({
    eventTypes: ['compliance_alert'],
    showToasts: true,
  });
}

export function useTeamActivity() {
  return useRealtimeUpdates({
    eventTypes: ['team_activity'],
    showToasts: false, // Usually too noisy
  });
}

interface RealtimeSubscriptionOptions {
  table: string;
  event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
  filter?: string;
  schema?: string;
  onUpdate?: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
}

/**
 * Lightweight helper hook for subscribing directly to Supabase realtime changes.
 * Keeps existing APIs in hooks that previously consumed useRealtimeSubscription.
 */
export function useRealtimeSubscription({
  table,
  event = '*',
  filter,
  schema = 'public',
  onUpdate,
}: RealtimeSubscriptionOptions) {
  useEffect(() => {
    const channel = supabase
      .channel(`realtime:${schema}:${table}:${event}:${filter ?? 'all'}`)
      .on(
        'postgres_changes' as any,
        { schema, table, event, filter } as any,
        (payload: any) => {
          onUpdate?.(payload);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [table, event, filter, schema, onUpdate]);
}
